import React from 'react';
import { ArrowLeftOutlined, CalendarOutlined, DollarOutlined, EnvironmentOutlined, GlobalOutlined, TeamOutlined } from '@ant-design/icons';
import { PrimaryButton } from '@components/atoms';
import { useJobPositions } from '@hooks';
import { Buildings } from '@phosphor-icons/react';
import { Alert, Button, Col, Divider, Row, Skeleton, Space, Tag, Typography } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;

export const CareerDetailsPage = () => {
  const { positionId } = useParams<{ positionId: string }>();
  const navigate = useNavigate();
  const { getJobPosition } = useJobPositions();

  // Get job position details
  const positionQuery = positionId ? getJobPosition(positionId) : null;

  // Handle back navigation
  const handleBack = () => {
    navigate('/careers');
  };

  // Handle apply button click
  const handleApply = (e: React.MouseEvent<HTMLButtonElement>) => {

      e.preventDefault(); 
      // Navega para a página de carreiras com a âncora
      navigate('/careers');                   
      // Pequeno timeout para garantir que a navegação complete antes de executar o ajuste
      setTimeout(() => {
        // Procura o elemento com ID 'application-form'
        const appForm = document.getElementById('application-form');
        if (appForm) {
          // Calcula a posição para exibir o título da seção (50px acima do elemento)
          const offset = appForm.getBoundingClientRect().top + window.pageYOffset - 50;
          // Faz a rolagem para a posição calculada
          window.scrollTo({
            top: offset,
            behavior: 'smooth'
          });
        }
      }, 300);
    
  };

  // Loading state
  if (positionQuery?.isLoading) {
    return (
      <div className="bg-black min-h-screen text-white py-10">
        <div className="container mx-auto px-4">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="mb-6 !text-white hover:!text-primary"
          >
            Back to careers
          </Button>
          <Skeleton active paragraph={{ rows: 12 }} />
        </div>
      </div>
    );
  }

  // Error state
  if (positionQuery?.isError || !positionQuery?.data) {
    return (
      <div className="bg-black min-h-screen text-white py-10">
        <div className="container mx-auto px-4">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="mb-6 !text-white hover:!text-primary"
          >
            Back to careers
          </Button>
          <Alert
            message="Position Not Found"
            description="The job position you're looking for could not be found. Please try another position or check back later."
            type="error"
            showIcon
            className="mb-8"
            action={
              <Button onClick={handleBack}>View all positions</Button>
            }
          />
        </div>
      </div>
    );
  }

  const position = positionQuery.data;

  return (
    <div className="bg-black min-h-screen text-white">
      {/* Hero Section */}
      <section className="w-full py-16 bg-zinc-900 relative">
        <div className="absolute inset-0 w-full h-full flex items-center justify-center">
          <img
            src="/assets/images/bg-careers.png"
            alt=""
            className="w-full h-full object-cover opacity-40"
          />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={handleBack}
            className="mb-6 !text-white hover:!text-primary"
          >
            Back to careers
          </Button>
          <Title level={1} className="!text-white !font-['Android_101'] !font-normal uppercase mb-4">{position.title}</Title>
          <div className="flex flex-wrap gap-4 mb-6 items-center">
            <div className="flex items-center">
              <EnvironmentOutlined className="mr-2 text-gray-400" />
              <Text className="!text-white !font-['Barlow']">{position.location}</Text>
            </div>
            <div className="flex items-center">
              <DollarOutlined className="mr-2 text-[#F74902]" />
              <Text className="!text-[#F74902] !font-['Barlow']">{position.salaryRange}</Text>
            </div>
            <div className="flex items-center">
              <CalendarOutlined className="mr-2 text-gray-400" />
              <Text className="!text-gray-400 !font-['Barlow']">Posted on {position.datePosted}</Text>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mb-6">
            <Tag className="!py-1 !px-4 !text-white !bg-transparent !border-white">{position.department}</Tag>
            <Tag className="!py-1 !px-4 !text-white !bg-transparent !border-white">{position.type}</Tag>
            {position.featured && (
              <Tag className="!py-1 !px-4 !text-black !bg-[#F74902] !border-none">Featured</Tag>
            )}
          </div>
        </div>
      </section>

      {/* Job Details Section */}
      <section className="w-full py-16 bg-second-black-carbon">
        <div className="container mx-auto px-4">
          <Row gutter={[48, 32]}>
            <Col xs={24} lg={16}>
              <div className="bg-zinc-900 p-8 mb-8">
                <Title level={3} className="!text-white !font-['Android_101'] !font-normal mb-6">JOB DESCRIPTION</Title>
                <Paragraph className="!text-gray-300 !font-['Barlow'] whitespace-pre-line mb-6 text-lg">
                  {position.description}
                </Paragraph>

                <Divider className="!bg-zinc-700 my-8" />

                <Title level={3} className="!text-white !font-['Android_101'] !font-normal mb-6">REQUIREMENTS</Title>
                <Paragraph className="!text-gray-300 !font-['Barlow'] mb-6 text-lg">
                  {(position.requirements && Array.isArray(position.requirements) && position.requirements.length > 0) ? (
                    <ul className="list-disc pl-6 space-y-2">
                      {position.requirements.map((requirement, index) => (
                        <li key={index}>{requirement}</li>
                      ))}
                    </ul>
                  ) : (
                    "No specific requirements listed for this position."
                  )}
                </Paragraph>

                <Divider className="!bg-zinc-700 my-8" />

                <Title level={3} className="!text-white !font-['Android_101'] !font-normal mb-6">BENEFITS</Title>
                <Paragraph className="!text-gray-300 !font-['Barlow'] mb-6 text-lg">
                  {(position.benefits && Array.isArray(position.benefits) && position.benefits.length > 0) ? (
                    <ul className="list-disc pl-6 space-y-2">
                      {position.benefits.map((benefit, index) => (
                        <li key={index}>{benefit}</li>
                      ))}
                    </ul>
                  ) : (
                    "No specific benefits listed for this position."
                  )}
                </Paragraph>
              </div>
            </Col>

            <Col xs={24} lg={8}>
              <div className="bg-zinc-900 p-8 mb-8">
                <Title level={4} className="!text-white !font-['Android_101'] !font-normal mb-4">COMPANY INFO</Title>
                <Space direction="vertical" size="large" className="w-full">
                  <div className="flex items-center">
                    <img src="/assets/logos/ruppert-logo.svg" alt="Ruppert Inc. Logo" className="w-12 h-12" />
                    <div className="ml-4">
                      <Text className="!text-white !font-['Barlow'] block">Ruppert Inc.</Text>
                      <Text className="!text-gray-400 !font-['Barlow'] text-sm">Construction & Excavation</Text>
                    </div>
                  </div>
                  <Divider className="!bg-zinc-700 my-4" />
                  <div>
                    <div className="flex items-center mb-4">
                      <EnvironmentOutlined className="text-gray-400 mr-3 text-xl" />
                      <Text className="!text-gray-300 !font-['Barlow']">Truckee, California</Text>
                    </div>
                    <div className="flex items-center mb-4">
                      <TeamOutlined className="text-gray-400 mr-3 text-xl" />
                      <Text className="!text-gray-300 !font-['Barlow']">50-100 employees</Text>
                    </div>
                    <div className="flex items-center mb-4">
                      <Buildings className="text-gray-400 mr-3" weight="fill" size={20} />
                      <Text className="!text-gray-300 !font-['Barlow']">Construction & Excavation</Text>
                    </div>
                    <div className="flex items-center">
                      <GlobalOutlined className="text-gray-400 mr-3" style={{ fontSize: '20px' }} />
                      <Text className="!text-gray-300 !font-['Barlow']">
                        <a href="https://www.ruppertinc.com" target="_blank" rel="noopener noreferrer" className="!text-gray-300 !font-['Barlow'] hover:!text-[#F74902] hover:underline">
                          www.ruppertinc.com
                        </a>
                      </Text>
                    </div>
                  </div>
                </Space>
              </div>

              <div className="bg-zinc-900 p-8">
                <Title level={4} className="!text-white !font-['Android_101'] !font-normal mb-4">JOIN OUR TEAM</Title>
                <div className="flex justify-center">
                  <PrimaryButton
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => handleApply(e)}
                    className="!px-8 !py-3 !h-auto !font-['Android_101'] !text-base w-full"
                  >
                    APPLY NOW
                  </PrimaryButton>
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </section>

      {/* Similar Positions Section */}
      <section className="w-full py-16 bg-zinc-900">
        <div className="container mx-auto px-4">
          <Title level={2} className="!text-white !font-['Android_101'] !font-normal mb-8 text-center">View all positions available</Title>
          <Paragraph className="!text-gray-300 !font-['Barlow'] text-center mb-10">
            Explore other opportunities that might interest you
          </Paragraph>

          {/* {similarPositionsQuery?.isLoading ? (
            <Row gutter={[24, 24]} className="mb-10">
              {[1, 2, 3].map(index => (
                <Col xs={24} md={8} key={index}>
                  <Skeleton active paragraph={{ rows: 4 }} />
                </Col>
              ))}
            </Row>
          ) : similarPositionsQuery?.data && similarPositionsQuery.data.length > 0 ? (
            <Row gutter={[24, 24]} className="mb-10">
              {similarPositionsQuery.data.map(similarPosition => (
                <Col xs={24} md={8} key={similarPosition.id}>
                  <div className="bg-second-black-carbon p-6">
                    <Title level={5} className="!text-white !font-['Android_101'] !font-normal mb-4">{similarPosition.title.toUpperCase()}</Title>
                    <div className="flex items-center mb-4">
                      <EnvironmentOutlined className="mr-2 text-gray-400" />
                      <Text className="!text-white !font-['Barlow']">{similarPosition.location}</Text>
                    </div>
                    <Tag className="mb-8 !bg-transparent !border-white !text-white">{similarPosition.department}</Tag>
                    <Button
                      onClick={() => navigate(`/careers/${similarPosition.id}`)}
                      className="w-full border-white text-white hover:!bg-primary hover:!text-black hover:!border-primary mt-4"
                    >
                      VIEW DETAILS
                    </Button>
                  </div>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="text-center mb-8">
              <Paragraph className="!text-gray-300 !font-['Barlow']">
                No similar positions found at the moment.
              </Paragraph>
            </div>
          )} */}

          <div className="text-center">
            <PrimaryButton
              onClick={handleBack}
              className="!px-8 !py-3 !h-auto !font-['Android_101'] !text-base"
            >
              VIEW ALL POSITIONS
            </PrimaryButton>
          </div>
        </div>
      </section>

    </div>
  );
};
